import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../lib/AuthContext";
import { apiGet } from "../lib/api";
import { COLORS } from "../lib/colors";
import MetricsBar from "./MetricsBar";
import StudentList from "./StudentList";
import SearchTermsChart from "./SearchTermsChart";
import ActivityFeed from "./ActivityFeed";
import PendingRecruiters from "./PendingRecruiters";
import RecruiterTable from "./RecruiterTable";
import EngagementReports from "./EngagementReports";
import ArchiveSection from "./ArchiveSection";
import EventsLog from "./EventsLog";

export default function DashboardTab() {
  const { session, schoolId, profile } = useAuth();
  const isPlatformAdmin = !profile?.school_id;
  const token = session?.access_token;

  const [overview, setOverview] = useState(null);
  const [students, setStudents] = useState(null);
  const [searchTerms, setSearchTerms] = useState(null);
  const [activity, setActivity] = useState(null);
  const [pendingRecruiters, setPendingRecruiters] = useState(null);
  const [recruiters, setRecruiters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setError("");

    try {
      const schoolParam = schoolId ? `?school_id=${schoolId}` : "";

      const [overviewRes, studentsRes, termsRes, activityRes, pendingRes, recruitersRes] = await Promise.all([
        apiGet(`/dashboard/overview${schoolParam}`, token),
        apiGet(`/dashboard/students${schoolParam}`, token),
        apiGet("/dashboard/search_terms", token),
        apiGet(`/dashboard/activity${schoolParam}`, token),
        apiGet("/dashboard/pending_recruiters", token),
        apiGet("/dashboard/recruiters", token),
      ]);

      setOverview(overviewRes);
      setStudents(studentsRes.students || []);
      setSearchTerms(termsRes.terms || []);
      setActivity(activityRes.activity || []);
      setPendingRecruiters(pendingRes.pending || []);
      setRecruiters(recruitersRes.recruiters || []);
    } catch (err) {
      setError(`Failed to load dashboard: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [token, schoolId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <p style={{ fontSize: 14, color: COLORS.textMuted }}>Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: "14px 18px", borderRadius: 8,
        backgroundColor: COLORS.redLight, color: COLORS.red, fontSize: 14,
      }}>
        {error}
        <button
          onClick={() => { setLoading(true); fetchAll(); }}
          style={{
            marginLeft: 16, padding: "4px 12px", borderRadius: 6, border: "none",
            backgroundColor: COLORS.red, color: "#fff", fontSize: 13,
            fontFamily: "inherit", cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>
        Careers Team Dashboard
      </h2>
      <p style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 24 }}>
        Overview of your talent pool, recruiter activity, and engagement
      </p>

      {/* Metrics */}
      <MetricsBar data={overview} />

      <div style={{ height: 1, backgroundColor: COLORS.border, margin: "24px 0" }} />

      {/* Two columns: Students + Search Terms / Activity */}
      <div className="dashboard-two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
        <StudentList students={students} token={token} onRefresh={fetchAll} />
        <div>
          <SearchTermsChart terms={searchTerms} />
          <div style={{ height: 1, backgroundColor: COLORS.border, margin: "20px 0" }} />
          <ActivityFeed activity={activity} />
        </div>
      </div>

      <div style={{ height: 1, backgroundColor: COLORS.border, margin: "24px 0" }} />

      {/* Pending Recruiters */}
      <PendingRecruiters pending={pendingRecruiters} token={token} onRefresh={fetchAll} />

      <div style={{ height: 1, backgroundColor: COLORS.border, margin: "24px 0" }} />

      {/* Recruiter Table */}
      <RecruiterTable recruiters={recruiters} token={token} onRefresh={fetchAll} />

      <div style={{ height: 1, backgroundColor: COLORS.border, margin: "24px 0" }} />

      {/* Engagement Reports */}
      <EngagementReports token={token} />

      <div style={{ height: 1, backgroundColor: COLORS.border, margin: "24px 0" }} />

      {/* Year-End Archive */}
      <ArchiveSection token={token} schoolId={schoolId} onRefresh={fetchAll} />

      {/* Events Log — platform admins only */}
      {isPlatformAdmin && (
        <>
          <div style={{ height: 1, backgroundColor: COLORS.border, margin: "24px 0" }} />
          <EventsLog />
        </>
      )}
    </div>
  );
}
